import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";

interface FilterSectionProps {
  title: string;
  options: string[];
  selectedValues: string[];
  mode: "include" | "exclude";
  onChange: (values: string[], mode: "include" | "exclude") => void;
}

export function FilterSection({
  title,
  options,
  selectedValues,
  mode,
  onChange,
}: FilterSectionProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const toggleValue = (val: string) => {
    const newValues = selectedValues.includes(val)
      ? selectedValues.filter((v) => v !== val)
      : [...selectedValues, val];
    onChange(newValues, mode);
  };

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Switch
            id={`mode-${title}`}
            checked={mode === "exclude"}
            onCheckedChange={(checked) =>
              onChange(selectedValues, checked ? "exclude" : "include")
            }
            className="cursor-pointer"
          />
          <Label htmlFor={`mode-${title}`} className="text-xs">
            {mode === "exclude" ? "Exclude" : "Include"}
          </Label>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs"
          onClick={() => onChange([], mode)}
        >
          Clear
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <div className="max-h-[200px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
        {filteredOptions.map((option) => (
          <div key={option} className="flex items-center space-x-2">
            <Checkbox
              id={`${title}-${option}`}
              checked={selectedValues.includes(option)}
              onCheckedChange={() => toggleValue(option)}
              className="cursor-pointer"
            />
            <label
              htmlFor={`${title}-${option}`}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer truncate cursor-pointer"
            >
              {option}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
